import type {
    EstateExpandOpts,
    FormSchema,
    GanttChartData,
    IAssetRentRoll,
    IBaseDDQuestion,
    ICreateMFHParams,
    ICreateMfhEstateRequest,
    IDDAnswer,
    IDDAnswerUpsertParams,
    IDDQuestion,
    IEstarCoreEstate,
    IEstate,
    IEstateRentRoll,
    IInfasData,
    ILocationDTO,
    IMFH,
    IMFHBuilding,
    IMFHElement,
    IMFHExternalUnit,
    IMFHPortfolio,
    IMFHProjectPlan,
    IMFHSummaryData,
    IMfhBuildingQuery,
    IMfhBuildingRequestParams,
    IMfhFilter,
    IMfhPipelineFilterConfig,
    IMfhPortfolioQuery,
    IMfhStats,
    IPaginationFields,
    IPipelineMFH,
    IPipelineMFHPortfolio,
    IRentRoll,
    ITaskResponse,
    MFHBuildingExpandOpts,
    MFHElementType,
    MFHExpandOpts,
    MFHPortfolioExpandOpts,
    MFHProjectPlanExpandOpts,
    MFHSourcingState,
    TaskExpandOpts,
} from '@condo/domain';
import axios from 'axios';
import { type ICockpitAxiosRequestConfig, getClient } from '../api-client';
import { getSignedUrl, publishFile } from '../basecamp.api';

const skipErrorHandler: ICockpitAxiosRequestConfig = { skipErrorHandler: true };

export const maintenanceDeleteMFH = async (params: { dryRun?: boolean; mfhId: number; confirmDeletion: boolean }): Promise<any> => {
    const { data } = await getClient('basecampMaintenance').post(
        '/maintenance/delete-mfh',
        {
            mfhId: params.mfhId,
            confirmDeletion: params.confirmDeletion,
        },
        { headers: params.dryRun ? { 'x-dry-run': true } : undefined },
    );

    return data;
};

export const createMFHsViaCSV = async (file: File): Promise<any> => {
    const signedUrl = await getSignedUrl();

    await axios
        .create({
            headers: {
                'Content-Type': file.type,
                'Access-Control-Allow-Origin': '*',
            },
        })
        .put(signedUrl.url, file);

    const { path } = await publishFile(signedUrl.bucketFilename);

    return getClient('basecamp')
        .post('/mfh/csv', { csvURL: path })
        .then(response => response.data);
};

export const fetchMfhList = async (
    filter: IMfhFilter,
    expandOptions?: MFHExpandOpts,
): Promise<{
    mfhs: IMFH[];
    total: number;
}> =>
    getClient('basecamp')
        .get('/mfh', { params: { ...filter, expand: expandOptions } })
        .then(response => response.data);

export const fetchMfhSourcingList = async (
    filter?: IMfhFilter,
): Promise<{
    mfhs: IMFH[];
    total: number;
}> =>
    getClient('basecamp')
        .get('/mfh-sourcing-list', { params: filter })
        .then(response => response.data);

export const fetchEligibleAssetsList = async (
    filter?: IMfhFilter,
): Promise<{
    mfhs: IMFH[];
    total: number;
}> =>
    getClient('basecamp')
        .get('/eligible-assets', { params: filter })
        .then(response => response.data);

export const fetchAssetsInPipelineList = async (
    filter?: IMfhFilter,
    includeCandidateAssetPartners?: boolean,
): Promise<{
    mfhs: IPipelineMFH[];
    total: number;
}> =>
    getClient('basecamp')
        .get('/assets-in-pipeline', {
            params: { ...filter, includeCandidateAssetPartners },
        })
        .then(response => response.data);

export const fetchMfhPipelineFilteredConfig = async (filters: {
    city?: string[];
    sourcingState?: MFHSourcingState[];
    assetPartnerId?: number[];
    candidateAssetPartnerId?: number[];
    mfhPortfolioId?: number[];
}): Promise<IMfhPipelineFilterConfig> =>
    getClient('basecamp')
        .get('/assets-in-pipeline/config', { params: filters })
        .then(response => response.data);

export const fetchOneMfh = async (opts: { mfhId: number; expandOpts?: MFHExpandOpts }): Promise<IMFH> =>
    getClient('basecamp')
        .post(`/mfh/${opts.mfhId}`, { expand: opts.expandOpts })
        .then(response => response.data);

export const getMFHSummary = async (mfhId: number, candidateAssetPartnerId?: number): Promise<IMFHSummaryData> =>
    getClient('basecamp')
        .get(`/mfh/${mfhId}/summary`, { params: candidateAssetPartnerId ? { candidateAssetPartnerId } : {} })
        .then(response => response.data.mfh);

export const getMfhEstates = async (mfhId: number, expandOptions: EstateExpandOpts): Promise<IEstate[]> =>
    getClient('basecamp')
        .get(`/mfh/${mfhId}/estates`, {
            params: {
                expand: expandOptions,
            },
        })
        .then(response => response.data);

export const getMfhTasks = async (mfhId: number, expandOptions: TaskExpandOpts): Promise<ITaskResponse> =>
    getClient('basecamp')
        .get(`/mfh/${mfhId}/tasks`, {
            params: {
                expand: expandOptions,
            },
        })
        .then(response => response.data);

export const getMfhTaskMilestoneReport = async (mfhId: number): Promise<any[]> =>
    getClient('basecamp')
        .get(`/mfh/${mfhId}/task-milestone-report`)
        .then(response => response.data);

export const getMfhTaskHierarchy = async (mfhId: number): Promise<IMFH> =>
    getClient('basecamp')
        .get(`/mfh/${mfhId}/task-hierarchy`)
        .then(response => response.data);

export const createMFH = async (body: ICreateMFHParams): Promise<IMFH> =>
    getClient('basecamp')
        .post('/mfh', body)
        .then(response => response.data);

export const createMFHEstate = async (payload: ICreateMfhEstateRequest): Promise<IEstate> =>
    getClient('basecamp')
        .post(`/mfh/buildings/${payload.mfhBuildingId}/estates`, {
            locationId: payload.locationId,
            attributes: payload.attributes,
            rentableUnitState: payload.rentableUnitState,
            startRenovationProcess: payload.startRenovationProcess,
        })
        .then(response => response.data.estate);

export const updateMFH = async (mfhID: number, body: Partial<IMFH>): Promise<IMFH> =>
    getClient('basecamp')
        .patch(`/mfh/${mfhID}`, body)
        .then(response => response.data);

export const upsertMFHBuildingLocation = async (mfhBuildingId: number, location: ILocationDTO): Promise<void> =>
    getClient('basecamp').put(`/mfh/buildings/${mfhBuildingId}/locations`, { location });

export const upsertMFHBuilding = async (mfhId: number, { mfhBuilding, location }: IMfhBuildingRequestParams): Promise<IMFHBuilding> =>
    getClient('basecamp')
        .put(`/mfh/${mfhId}/buildings/`, { mfhBuilding, location })
        .then(res => res.data.mfhBuilding);

export const deleteMFHBuilding = async (mfhId: number, mfhBuildingId: number): Promise<IMFHBuilding> =>
    getClient('basecamp').delete(`/mfh/${mfhId}/buildings/${mfhBuildingId}`);

export const moveEstatesToBuildingLocation = async (payload: { estateIds: number[]; mfhBuildingId: number }): Promise<void> =>
    getClient('basecamp').patch(`/mfh/estates/move/building/${payload.mfhBuildingId}`, {
        estateIds: payload.estateIds,
    });

export const getMfhDDQuestions = async <const TSchema extends Array<FormSchema> = Array<FormSchema>>(
    mfhId: number,
    type: TSchema,
): Promise<IDDQuestion<TSchema extends Array<infer U> ? U : never>[]> =>
    getClient('basecamp')
        .get(`/mfh/${mfhId}/dd/questions`, { params: { type } })
        .then(response => response.data.questions);

export const upsertMfhDDQuestion = async <const TSchema extends Array<FormSchema> = Array<FormSchema>>(
    question: IBaseDDQuestion,
): Promise<IDDQuestion<TSchema extends Array<infer U> ? U : never>[]> =>
    getClient('basecamp')
        .post(`/mfh/${question.mfhId}/dd/questions`, question)
        .then(response => response.data.questions);

export const deleteMfhDDQuestion = async <const TSchema extends Array<FormSchema> = Array<FormSchema>>(
    mfhId: number,
    questionId: number,
): Promise<IDDQuestion<TSchema extends Array<infer U> ? U : never>[]> =>
    getClient('basecamp')
        .delete(`/mfh/${mfhId}/dd/questions/${questionId}`)
        .then(response => response.data.questions);

export const updateMfhDDAnswer = async (mfhId: number, data: IDDAnswerUpsertParams): Promise<IDDAnswer> =>
    getClient('basecamp')
        .put(`/mfh/${mfhId}/dd/answers`, data)
        .then(response => response.data.answer);

export const getMfhAttributesFromInfas = async (mfhId: number): Promise<IInfasData> =>
    getClient('basecamp')
        .get(`/mfh/${mfhId}/infas-attributes`)
        .then(response => response.data);

export const fetchMfhRentRoll = async (mfhId: number): Promise<IEstateRentRoll[]> =>
    getClient('basecamp')
        .get(`/mfh/${mfhId}/rent-roll`)
        .then(response => response.data.data);

export const getRentRollImportSheetUrl = async (mfhId: number): Promise<string> =>
    getClient('basecamp')
        .get(`/mfh/${mfhId}/rent-roll-dd/import-sheet-url`, skipErrorHandler)
        .then(response => response.data.url);

export const getMfhElementImportSheetUrl = async (type: MFHElementType): Promise<string> =>
    getClient('basecamp')
        .get('/mfh/elements/import-sheet-url', { params: { type } })
        .then(response => response.data.url);

export const parseMfhElementsFromSheet = async (spreadsheetUrl: string, type: MFHElementType): Promise<any[]> =>
    getClient('basecamp')
        .post('/mfh/elements/parse-from-sheet', { spreadsheetUrl, type })
        .then(response => response.data.data);

export const getMfhElements = async (mfhId: number, params?: { type?: MFHElementType[]; expand?: any }): Promise<IMFHElement[]> =>
    getClient('basecamp')
        .get(`/mfh/${mfhId}/elements`, { params })
        .then(response => response.data.elements);

export const saveMfhElements = async (mfhId: number, elements: Partial<IMFHElement>[]): Promise<IMFHElement[]> =>
    getClient('basecamp')
        .post(`/mfh/${mfhId}/elements`, { elements })
        .then(response => response.data.elements);

export const updateMfhElement = async (mfhElementId: number, data: Partial<IMFHElement>): Promise<IMFHElement> =>
    getClient('basecamp')
        .patch(`/mfh/elements/${mfhElementId}`, { data })
        .then(response => response.data.element);

export const deleteMfhElements = async (mfhElementIds: number[]): Promise<void> =>
    getClient('basecamp')
        .delete('/mfh/elements', { data: { mfhElementIds } })
        .then(response => response.data);

export const parseRentRollFromSheet = async (mfhId: number, spreadsheetUrl: string): Promise<IRentRoll[]> =>
    getClient('basecamp')
        .post(`/mfh/${mfhId}/rent-roll-dd/parse`, { spreadsheetUrl }, skipErrorHandler)
        .then(response => response.data.data);

export const parseRentRollFromInvestmentCaseSheet = async (mfhId: number, spreadsheetUrl: string): Promise<{ data: IRentRoll[] }> =>
    getClient('basecamp')
        .post(`/mfh/${mfhId}/rent-roll-dd/parse-from-investment-case`, { spreadsheetUrl }, skipErrorHandler)
        .then(response => ({ data: response.data.data }));

export const fetchMfhPortfolioList = async (
    query: IMfhPortfolioQuery,
    expand: MFHPortfolioExpandOpts,
    pagination?: IPaginationFields,
): Promise<{
    mfhPortfolios: IMFHPortfolio[];
    total: number;
}> =>
    getClient('basecamp')
        .get('/mfh-portfolios', { params: { query, expand, pagination } })
        .then(response => response.data);

export const fetchPipelineMfhPortfolios = async (
    filter?: IMfhFilter,
): Promise<{ portfolios: IPipelineMFHPortfolio[]; mfhsWithoutPortfolio: IPipelineMFH[]; total: number }> =>
    getClient('basecamp')
        .get('/pipeline-mfh-portfolios', { params: filter })
        .then(response => response.data);

export const fetchOneMfhPortfolio = async (mfhPortfolioId: number, expandOptions?: MFHPortfolioExpandOpts): Promise<IMFHPortfolio> =>
    getClient('basecamp')
        .get(`/mfh-portfolio/${mfhPortfolioId}`, {
            params: {
                expand: expandOptions,
            },
        })
        .then(response => response.data);

export const fetchMfhsOfPortfolio = async (mfhPortfolioId: number): Promise<{ mfhs: IMFH[] }> =>
    getClient('basecamp')
        .get(`/mfh-portfolio/${mfhPortfolioId}/mfhs`)
        .then(response => response.data);

export const createMFHPortfolio = async (payload: { name: string; mfhIds: number[]; assetPartnerId: number }): Promise<IMFHPortfolio> =>
    getClient('basecamp')
        .post('/mfh-portfolio', payload)
        .then(response => response.data);

export const updateMFHPortfolio = async (mfhPortfolioId: number, payload: { name?: string; mfhIds?: number[] }): Promise<IMFHPortfolio> =>
    getClient('basecamp')
        .patch(`/mfh-portfolio/${mfhPortfolioId}`, payload)
        .then(response => response.data);

export const fetchMfhBuilding = async (mfhBuildingId: number, expand: MFHBuildingExpandOpts): Promise<IMFHBuilding> =>
    getClient('basecamp')
        .get(`/mfh/buildings/${mfhBuildingId}`, { params: { expand } })
        .then(response => response.data.mfhBuilding);

export const fetchMfhBuildings = async (mfhId: number, expand: MFHBuildingExpandOpts): Promise<IMFHBuilding[]> =>
    getClient('basecamp')
        .get(`/mfh/${mfhId}/buildings`, { params: { expand } })
        .then(response => response.data.mfhBuildings);

export const fetchAllMfhBuildings = async (query: IMfhBuildingQuery, expand: MFHBuildingExpandOpts, pagination?: IPaginationFields): Promise<IMFHBuilding[]> =>
    getClient('basecamp')
        .get(`/mfh-buildings`, { params: { query, expand, pagination } })
        .then(response => response.data);

export const fetchMfhHierarchy = async (mfhId: number): Promise<IMFH> =>
    getClient('basecamp')
        .get(`/mfh/${mfhId}/mfh-hierarchy`)
        .then(response => response.data);

export const getMFHEstarCoreEstates = async (mfhId: number): Promise<IEstarCoreEstate[]> =>
    getClient('basecamp')
        .get(`/mfh/${mfhId}/estarcore-estates`)
        .then(response => response.data.estarCoreEstates);

export const getEstarCoreMFHIds = async (): Promise<string[]> =>
    getClient('basecamp')
        .get(`/mfh/estarcore-mfh-ids`)
        .then(response => response.data.estarCoreMFHIds);

export const getMfhExternalUnits = async (mfhId: number): Promise<IMFHExternalUnit[]> =>
    getClient('basecamp')
        .get(`/mfh/${mfhId}/external-units`)
        .then(response => response.data.mfhExternalUnits);

export const getAssetRentRoll = async (mfhId: number): Promise<IAssetRentRoll[]> =>
    getClient('basecamp')
        .get(`/mfh/${mfhId}/asset-rent-roll`)
        .then(response => response.data.assetRentRoll);

export const getMfhStats = async (mfhId: number): Promise<IMfhStats> =>
    getClient('basecamp')
        .get(`/mfh/${mfhId}/stats`)
        .then(response => response.data);

export const getMfhProjectPlanSnapshots = async (mfhId: number, params?: { expand?: MFHProjectPlanExpandOpts }): Promise<IMFHProjectPlan[]> =>
    getClient('basecamp')
        .get(`/mfh/${mfhId}/project-plan-snapshots`, { params })
        .then(response => response.data);

export const createMfhProjectPlanSnapshot = async (mfhId: number, ganttData: GanttChartData, description?: string): Promise<IMFHProjectPlan> =>
    getClient('basecamp')
        .post(`/mfh/${mfhId}/project-plan-snapshot`, { ganttData, description })
        .then(response => response.data);

export const deleteMfhProjectPlanSnapshot = async (mfhProjectPlanId: number): Promise<void> =>
    getClient('basecamp')
        .delete(`/mfh/project-plan-snapshot/${mfhProjectPlanId}`)
        .then(response => response.data);
